import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "global-container" }
const _hoisted_3 = { class: "header-container" }
const _hoisted_4 = {
  key: 1,
  class: "chips-container"
}
const _hoisted_5 = { class: "mt-2 chips" }
const _hoisted_6 = { class: "mt-4 mb-2 chips" }
const _hoisted_7 = {
  key: 0,
  class: "mt-5"
}
const _hoisted_8 = { class: "mb-3 item-container" }
const _hoisted_9 = { style: { flex: '1 1 0%' } }
const _hoisted_10 = { class: "actions-container" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { class: "text-caption" }

import HeaderComponent from '@/components/home/HeaderComponent.vue'
import { ref, onMounted, watch } from 'vue'
import { firestore as db } from '@/main'
import { collection, getDocs, orderBy, query, deleteDoc, doc, updateDoc, addDoc } from 'firebase/firestore'
import { Lecture, LectureRequest } from '@/types/Lecture'
import { useAuthStore } from '@/stores/AuthStateStore'
import { storeToRefs } from 'pinia'
import CommonProgress from '@/components/common/CommonProgress.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'LecturesView',
  setup(__props) {

const lecturesQuery = query(collection(db, 'Coarses'), orderBy('year', 'desc'))
const lecturesList = ref<Lecture[]>([])
const filteredLectures = ref<Lecture[]>([])

const yearsList = ref<string[]>([])
const selectedYear = ref('')

const semestersList = ref<string[]>(['Spring', 'Summer', 'Fall', 'Winter'])
const selectedSemester = ref(getCurrentSemester())
const { isSignedIn, isAdmin } = storeToRefs(useAuthStore())

const isLoading = ref(false)
const isUploading = ref(false)
const isEditMode = ref(false)
const showAddModal = ref(false)

const searchText = ref('')
const selectedId = ref('')
const newLectureModel = ref<LectureRequest>({
  title: '',
  year: new Date().getFullYear().toString(),
  semester: getCurrentSemester(),
  graduate: '0'
})

function getCurrentSemester () {
  const month = new Date().getMonth() + 1
  if (month >= 3 && month <= 6) {
    return 'Spring'
  } else if (month === 7 || month === 8) {
    return 'Summer'
  } else if (month >= 9 && month <= 12) {
    return 'Fall'
  } else {
    return 'Winter'
  }
}

function filterLectures () {
  filteredLectures.value = lecturesList.value.filter(lect => {
    return lect.year === selectedYear.value && lect.semester === selectedSemester.value
  })

  if (filteredLectures.value.length === 0 && lecturesList.value.length > 0 && lecturesList.value.some(lect => lect.year === selectedYear.value)) {
    selectedSemester.value = lecturesList.value.find(lect => lect.year === selectedYear.value)?.semester || 'Spring'
  }
}

function filterYears () {
  yearsList.value = Array.from(new Set(lecturesList.value.map(lect => lect.year))).sort((a, b) => parseInt(b) - parseInt(a))
}

function uploadLecture () {
  if (newLectureModel.value.title === '' || newLectureModel.value.year === '' || newLectureModel.value.semester === '') {
    alert('Please fill in all fields.')
    return
  }

  isUploading.value = true

  if (isEditMode.value && selectedId.value !== '') {
    updateDoc(doc(db, 'Coarses', selectedId.value), {
      ...newLectureModel.value
    })
      .then(() => {
        alert('Lecture updated successfully.')
        const index = lecturesList.value.findIndex(p => p.id === selectedId.value)
        if (index !== -1) {
          lecturesList.value[index] = { id: selectedId.value, ...newLectureModel.value }
          filterLectures()
        }

        filterYears()
        selectedYear.value = newLectureModel.value.year
        selectedSemester.value = newLectureModel.value.semester
        isEditMode.value = false
        selectedId.value = ''
        showAddModal.value = false
      })
      .catch((e: Error) => {
        console.log(e.message)
        alert(`An error occurred while updating the lecture: ${e.message}`)
      })
      .finally(() => {
        isUploading.value = false
      })
  } else {
    addDoc(collection(db, 'Coarses'), {
      ...newLectureModel.value
    })
      .then((doc) => {
        alert('Lecture created successfully.')
        lecturesList.value.push({
          id: doc.id,
          ...newLectureModel.value
        } as Lecture)

        filterLectures()
        filterYears()
        selectedYear.value = newLectureModel.value.year
        selectedSemester.value = newLectureModel.value.semester
        showAddModal.value = false
      })
      .catch((e: Error) => {
        console.log(e.message)
        alert(`An error occurred while creating the lecture: ${e.message}`)
      })
      .finally(() => {
        isUploading.value = false
      })
  }
}

function deleteLecture (lect: Lecture) {
  if (confirm(`Are you sure you want to delete ${lect.title} lecture?\nThis action cannot be undone.`)) {
    isLoading.value = true

    deleteDoc(doc(db, 'Coarses', lect.id))
      .then(() => {
        alert('Lecture deleted successfully.')
        lecturesList.value = lecturesList.value.filter(l => l.id !== lect.id)
        filterLectures()
        filterYears()

        if (!yearsList.value.includes(selectedYear.value) && yearsList.value.length > 0) {
          selectedYear.value = yearsList.value[0]
        }
      })
      .catch((e: Error) => {
        console.log(e.message)
        alert(`An error occurred while deleting the lecture: ${e.message}`)
      })
      .finally(() => {
        isLoading.value = false
      })
  }
}

function getLectures () {
  isLoading.value = true
  getDocs(lecturesQuery)
    .then((docs) => {
      lecturesList.value = docs.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Lecture[]
      filteredLectures.value = lecturesList.value

      filterYears()
      selectedYear.value = yearsList.value[0]
      filterLectures()
    })
    .catch((e: Error) => {
      console.log(e.message)
    })
    .finally(() => {
      isLoading.value = false
    })
}

watch([selectedSemester, selectedYear], () => {
  filterLectures()
})

watch(showAddModal, () => {
  if (!showAddModal.value) {
    newLectureModel.value = {
      title: '',
      year: new Date().getFullYear().toString(),
      semester: getCurrentSemester(),
      graduate: '0'
    }
  }

  isUploading.value = false
})

watch(searchText, () => {
  if (searchText.value === '') {
    filterLectures()
  } else {
    const searchLower = searchText.value.toLowerCase()
    filteredLectures.value = lecturesList.value.filter(lect => lect.title.toLowerCase().includes(searchLower))
  }
})

onMounted(() => {
  getLectures()
})


return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_card_contents = _resolveComponent("v-card-contents")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_container, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", null, [
            _createVNode(HeaderComponent, {
              title: 'Lectures',
              onOnClick: _cache[0] || (_cache[0] = ($event: any) => (showAddModal.value = true))
            })
          ]),
          _createVNode(_component_v_text_field, {
            class: "mt-5 search-bar",
            modelValue: searchText.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchText).value = $event)),
            label: "Search Lectures",
            color: "primary",
            "prepend-inner-icon": "mdi-magnify",
            variant: "outlined",
            "clear-icon": "mdi-close",
            clearable: "",
            "onClick:clear": _cache[2] || (_cache[2] = ($event: any) => (searchText.value = ''))
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_3, [
            (isLoading.value)
              ? (_openBlock(), _createBlock(CommonProgress, { key: 0 }))
              : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(yearsList.value, (year) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: "ml-2 mt-2",
                        key: year
                      }, [
                        (selectedYear.value === year && searchText.value === '')
                          ? (_openBlock(), _createBlock(_component_v_chip, {
                              key: 0,
                              "prepend-icon": "mdi-check",
                              variant: "tonal",
                              color: "primary",
                              class: "rounded-pill"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(year), 1)
                              ]),
                              _: 2
                            }, 1024))
                          : (_openBlock(), _createBlock(_component_v_chip, {
                              key: 1,
                              onClick: ($event: any) => (selectedYear.value = year),
                              variant: "tonal",
                              class: "rounded-pill",
                              disabled: searchText.value !== '' || filteredLectures.value.length === 0
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(year), 1)
                              ]),
                              _: 2
                            }, 1032, ["onClick", "disabled"]))
                      ]))
                    }), 128))
                  ]),
                  _createElementVNode("div", _hoisted_6, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(semestersList.value, (semester) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: "ml-2 mt-2",
                        key: semester
                      }, [
                        (selectedSemester.value === semester && searchText.value === '')
                          ? (_openBlock(), _createBlock(_component_v_chip, {
                              key: 0,
                              "prepend-icon": "mdi-check",
                              variant: "tonal",
                              color: "primary",
                              class: "rounded-pill"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(semester), 1)
                              ]),
                              _: 2
                            }, 1024))
                          : (_openBlock(), _createBlock(_component_v_chip, {
                              key: 1,
                              onClick: ($event: any) => (selectedSemester.value = semester),
                              variant: "tonal",
                              class: "rounded-pill",
                              disabled: lecturesList.value.filter(lect => lect.semester === semester && lect.year === selectedYear.value).length === 0 || searchText.value !== ''
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(semester), 1)
                              ]),
                              _: 2
                            }, 1032, ["onClick", "disabled"]))
                      ]))
                    }), 128))
                  ]),
                  (filteredLectures.value.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                        _createVNode(_component_font_awesome_icon, { icon: "fa-solid fa-xmark" }),
                        _cache[10] || (_cache[10] = _createTextVNode(" " + _toDisplayString('No lectures found'), -1))
                      ]))
                    : _createCommentVNode("", true)
                ]))
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredLectures.value, (lect) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "mt-5 contents-container",
              key: lect.id
            }, [
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  (searchText.value !== '')
                    ? (_openBlock(), _createBlock(_component_v_chip, {
                        key: 0,
                        class: "rounded-xl mr-2",
                        color: "primary",
                        variant: "tonal"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(lect.year) + " " + _toDisplayString(lect.semester), 1)
                        ]),
                        _: 2
                      }, 1024))
                    : _createCommentVNode("", true),
                  _createVNode(_component_v_chip, {
                    class: "rounded-xl mr-2",
                    color: "primary",
                    variant: "outlined"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(lect.graduate === '0' ? 'Undergraduate' : 'Graduate'), 1)
                    ]),
                    _: 2
                  }, 1024),
                  _createTextVNode(" " + _toDisplayString(lect.title), 1)
                ]),
                _createElementVNode("div", _hoisted_10, [
                  (_unref(isSignedIn) && _unref(isAdmin))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                        _createVNode(_component_v_btn, {
                          variant: "tonal",
                          class: "ml-2",
                          onClick: ($event: any) => {
                    {
                      isEditMode.value = true
                      selectedId.value = lect.id
                      newLectureModel.value = {
                        title: lect.title,
                        year: lect.year,
                        semester: lect.semester,
                        graduate: lect.graduate
                      };

                      showAddModal.value = true
                    }
                  }
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: "fa-solid fa-edit" })
                          ]),
                          _: 2
                        }, 1032, ["onClick"]),
                        _createVNode(_component_v_btn, {
                          variant: "tonal",
                          color: "red",
                          class: "ml-2",
                          onClick: ($event: any) => (deleteLecture(lect))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: "fa-solid fa-trash" })
                          ]),
                          _: 2
                        }, 1032, ["onClick"])
                      ]))
                    : _createCommentVNode("", true)
                ])
              ])
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_12, [
          _cache[11] || (_cache[11] = _createTextVNode(_toDisplayString('If you want to check the syllabus and lecture information, please use the Jeonbuk National University course registration system site.') + " ", -1)),
          _createVNode(_component_v_btn, {
            class: "ml-2",
            variant: "text",
            href: 'https://oasis.jbnu.ac.kr/jbnu/sugang/sbjt/sbjt.html?param=KOR'
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, { icon: "fa-solid fa-link" })
            ]),
            _: 1
          })
        ]),
        _createVNode(_component_v_dialog, {
          modelValue: showAddModal.value,
          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((showAddModal).value = $event)),
          class: "dialog"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, { class: "pa-4 card" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_title, { class: "rounded-xl title-container" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_row, { style: { alignItems: 'center', justifyContent: 'center', 'verticalAlign': 'middle' } }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(isEditMode.value ? 'Edit Lecture' : 'Add Lecture') + " ", 1),
                        _createVNode(_component_v_spacer),
                        _createVNode(_component_v_btn, {
                          variant: "text",
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (showAddModal.value = false)),
                          color: "red",
                          disabled: isUploading.value
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: "fa-solid fa-xmark" })
                          ]),
                          _: 1
                        }, 8, ["disabled"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card_contents, { class: "card-contents" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_text_field, {
                      class: "mt-4",
                      modelValue: newLectureModel.value.title,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((newLectureModel.value.title) = $event)),
                      label: "Title",
                      variant: "outlined",
                      color: "primary",
                      "prepend-icon": "mdi-format-title",
                      "clear-icon": "mdi-close",
                      clearable: "",
                      "onClick:clear": _cache[5] || (_cache[5] = ($event: any) => (newLectureModel.value.title = ''))
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_text_field, {
                      class: "mt-4",
                      modelValue: newLectureModel.value.year,
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((newLectureModel.value.year) = $event)),
                      label: "Year",
                      variant: "outlined",
                      color: "primary",
                      "prepend-icon": "mdi-calendar",
                      type: "number"
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_select, {
                      class: "mt-4",
                      modelValue: newLectureModel.value.semester,
                      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((newLectureModel.value.semester) = $event)),
                      items: semestersList.value,
                      label: "Semester",
                      variant: "outlined",
                      color: "primary",
                      "prepend-icon": "mdi-tree"
                    }, null, 8, ["modelValue", "items"]),
                    _createVNode(_component_v_checkbox, {
                      class: "mt-4",
                      modelValue: newLectureModel.value.graduate,
                      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((newLectureModel.value.graduate) = $event)),
                      "true-value": '1',
                      "false-value": '0',
                      label: "Graduate Course",
                      color: "primary",
                      "prepend-icon": "mdi-school"
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card_actions, null, {
                  default: _withCtx(() => [
                    (isUploading.value)
                      ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                          key: 0,
                          indeterminate: "",
                          color: "primary"
                        }))
                      : (_openBlock(), _createBlock(_component_v_btn, {
                          key: 1,
                          variant: "tonal",
                          color: "primary",
                          onClick: uploadLecture
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: "fa-solid fa-check" })
                          ]),
                          _: 1
                        }))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    })
  ]))
}
}

})